import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowLeft, Save, Loader2, Plus, Trash2, ArrowUp, ArrowDown, ImagePlus, Image as ImageIcon, AlertTriangle, X, CheckCircle2, Archive, Film, Info, RefreshCw, FlaskConical,
} from 'lucide-react';
import {
  SifaUnterweisung, SifaUnterweisungVorlage, UnterweisungAbschnitt, UnterweisungFrage, UnterweisungStatus,
} from '../../services/sifa/types';
import {
  MedienArt,
  loescheUnterweisungMedium,
  medienArt,
  medienUrl,
  updateUnterweisung,
  uploadUnterweisungMedium,
} from '../../services/sifa/sifaService';
import {
  VorlageKonfliktError,
  fetchVorlage,
  loescheVorlagenMedium,
  setzeVorlagenStatus,
  updateVorlage,
  uploadVorlagenMedium,
} from '../../services/sifa/vorlagenService';
import { vorlageMedienLoeschbar, vorlagenOrdner } from '../../services/sifa/vorlagenLogic';
import { describeError } from '../../services/errorMessage';
import {
  AM_ENDE,
  STATUS_LABEL,
  UnterweisungEntwurf,
  bereinigeEntwurf,
  darfAktivieren,
  entferneAn,
  entwurfAus,
  ersetzeAn,
  gueltigeAbschnittId,
  neueAntwort,
  neueFrage,
  neuerAbschnitt,
  pruefeEntwurf,
  verschiebe,
  verwaistePfade,
} from './unterweisungModulLogic';
import {
  FRAGE_ARCHIVIEREN,
  FRAGE_ERSTE_FREIGABE,
  FRAGE_ZURUECK_AUF_ENTWURF,
  FREIGEGEBEN_MELDUNG,
  KEIN_RECHT_HINWEIS,
  VORLAGEN_STATUS_LABEL,
  entwurfAusVorlage,
  freigabeHinweis,
  vorlageKopfAngaben,
} from './vorlagen/vorlagenViewLogic';
import { RueckfrageDialog } from './vorlagen/RueckfrageDialog';
import { TestLinkDialog } from './TestLinkDialog';
import { FRAGE_ERST_SPEICHERN, FRAGE_TESTEN_BEI_KONFLIKT, TEST_TOOLTIP, testAblauf } from './testLinkLogic';

interface Props {
  unterweisung: SifaUnterweisung;
  projectId: string;
  isReadOnly?: boolean;
  isMobile?: boolean;
  onZurueck: () => void;
  /** Nach jedem erfolgreichen Speichern — der Container haelt die Ablage aktuell. */
  onGespeichert: (u: SifaUnterweisung) => void;
}

interface VorlagenEditorProps {
  vorlage: SifaUnterweisungVorlage;
  /** Ohne Admin-Rolle und ohne Haekchen „SiFa": nur lesen. Wird je Render ausgewertet. */
  isReadOnly?: boolean;
  isMobile?: boolean;
  /** Vorschlaege fuer das Feld „Kategorie" — die vorhandenen Kategorien der Firma. */
  kategorien?: string[];
  onZurueck: () => void;
  /** Nach jedem Speichern, Statuswechsel und Neuladen — die Liste haelt sich damit aktuell. */
  onGespeichert: (v: SifaUnterweisungVorlage) => void;
}

// ─── Ziel (Adapter) ─────────────────────────────────────────────────────────

/** Was Kurs und Vorlage gemeinsam haben — mehr muss die Oberflaeche nicht wissen. */
type EditorQuelle = Pick<
  SifaUnterweisung,
  'id' | 'status' | 'titel' | 'beschreibung' | 'inhalt' | 'fragen' | 'bestehenProzent' | 'gueltigMonate' | 'dauerMin'
>;

interface ZielTexte {
  nurAnsicht: string;
  archiviert: string;
  /** Bleibender Hinweis unter der Kopfzeile; null = keiner. */
  kopfHinweis: string | null;
  aktivKnopf: string;
  wiederAktivKnopf: string;
  aktivErklaerung: string;
  aktivErfolg: string;
  /** Rueckfrage vor dem Aktivschalten; null = ohne. */
  frageAktiv: string | null;
  frageEntwurf: string;
  frageArchivieren: string;
}

interface Speicherauftrag {
  /** Bereinigt (bereinigeEntwurf), Titel geprueft. */
  entwurf: UnterweisungEntwurf;
  kategorie: string;
  status?: UnterweisungStatus;
  /** Gibt es ungespeicherte Aenderungen? Die Vorlage zaehlt ihre Fassung nur dann hoch. */
  inhaltGeaendert: boolean;
}

/**
 * Wohin der Editor schreibt (docs/sifa-vorlagen-plan.md §3d): ein Kurs beim
 * Kunden oder eine globale Vorlage. Die Oberflaeche ist EINE; was sich
 * unterscheidet — Speicherweg, Medienordner, Aufraeumen, Statusnamen,
 * Konflikt —, steckt im Ziel. Das Aufraeumen gehoert bewusst dem Ziel: nur
 * es kennt den eigenen Ordner (Medien-Regel §2, „Eigentum nach Ordner").
 */
interface EditorZiel<T extends EditorQuelle> {
  /** Der zuletzt gespeicherte Stand. */
  stand: T;
  ueberschrift: string;
  /** Weitere Angaben in der Kopfzeile, z. B. Fassung. */
  angaben: string[];
  statusTexte: Record<UnterweisungStatus, string>;
  schreibbar: boolean;
  /**
   * Vorlage: das Archiv ist umkehrbar („Wieder freigeben") und darum auch
   * aus dem Entwurf erreichbar. Ein Kurs beim Kunden bleibt archiviert
   * eingefroren — unter ihm stehen Zertifikate.
   */
  archivUmkehrbar: boolean;
  /** Projekt fragt wie bisher ueber den Browser, die Vorlage in der App. */
  browserRueckfrage: boolean;
  /** Art des Test-Links (sifa_testlink_anlegen) — getestet wird `stand.id`. */
  testArt: 'unterweisung' | 'vorlage';
  texte: ZielTexte;
  entwurfAus: (q: T) => UnterweisungEntwurf;
  /** Feld „Kategorie" (nur Vorlage). */
  kategorie?: { aus: (q: T) => string; vorschlaege: string[] };
  speichern: (auftrag: Speicherauftrag) => Promise<T>;
  /** Neuer Stand nach Speichern oder Neuladen — Container (und Adapter) merken ihn sich. */
  uebernehmen: (q: T) => void;
  hochladen: (file: File, fortschritt: (geladen: number, gesamt: number) => void) => Promise<{ path: string }>;
  /** Loescht Medien, auf die `nachher` nicht mehr zeigt — nur im eigenen Ordner. Best effort. */
  aufraeumen: (vorher: T, nachher: T) => Promise<void>;
  /** Optimistische Sperre (nur Vorlage): Konflikt erkennen, frischen Stand holen. */
  konflikt?: { erkennen: (e: unknown) => boolean; neuLaden: () => Promise<T> };
}

/** Gemerkt wird die ART der Rueckfrage, kein Callback — siehe nachfragen. */
type Rueckfrage =
  | { art: 'zurueck' }
  | { art: 'aktiv' }
  | { art: 'entwurf' }
  | { art: 'archivieren' }
  | { art: 'medium'; index: number; medium: MedienArt }
  | { art: 'abschnitt'; index: number }
  | { art: 'frage'; index: number }
  | { art: 'neuLaden' }
  | { art: 'testen' }
  | { art: 'testenKonflikt' };

/** Diese Rueckfragen kamen nach Nr. 141 dazu und fragen immer in der App, auch im Projekt. */
const NUR_IN_DER_APP: ReadonlySet<Rueckfrage['art']> = new Set(['neuLaden', 'testen', 'testenKonflikt']);

const UPLOAD_LAEUFT = 'Upload läuft';

const PROJEKT_TEXTE: ZielTexte = {
  nurAnsicht: 'Nur-Ansicht: Dieses Projekt ist schreibgeschützt geöffnet.',
  archiviert: 'Dieser Kurs ist archiviert und eingefroren.',
  kopfHinweis: null,
  aktivKnopf: 'Aktiv schalten',
  wiederAktivKnopf: 'Aktiv schalten',
  aktivErklaerung: 'Aktiv schalten speichert den Stand mit. Den Kundenlink schaltet man danach in der Ablage frei.',
  aktivErfolg: 'Der Kurs ist aktiv. In der Ablage lässt sich jetzt der Kundenlink aktivieren und als QR-Code ausgeben.',
  frageAktiv: null,
  frageEntwurf: 'Zurück auf Entwurf? Ein aktiver Kundenlink funktioniert dann nicht mehr, bis der Kurs wieder aktiv ist.',
  frageArchivieren: 'Kurs archivieren? Er lässt sich danach nicht mehr ändern; die Teilnahmen bleiben erhalten.',
};

/**
 * Der Editor eines Kurses beim Kunden. Aufruf und Verhalten wie vor Nr. 141;
 * neu ist nur der Ordnerfilter beim Aufraeumen (verwaistePfade mit
 * projectId): eine Kundenkopie zeigt auf die Medien der Vorlage, und wer dort
 * einen Abschnitt entfernt, darf nicht das Video aller anderen Kunden loeschen.
 */
export const UnterweisungEditor: React.FC<Props> = ({
  unterweisung,
  projectId,
  isReadOnly = false,
  isMobile = false,
  onZurueck,
  onGespeichert,
}) => {
  const ziel: EditorZiel<SifaUnterweisung> = {
    stand: unterweisung,
    ueberschrift: `Unterweisung Nr. ${unterweisung.nummer}`,
    angaben: [],
    statusTexte: STATUS_LABEL,
    schreibbar: !isReadOnly,
    archivUmkehrbar: false,
    browserRueckfrage: true,
    testArt: 'unterweisung',
    texte: PROJEKT_TEXTE,
    entwurfAus,
    speichern: ({ entwurf, status }) => updateUnterweisung(unterweisung.id, status ? { ...entwurf, status } : entwurf),
    uebernehmen: onGespeichert,
    hochladen: (file, fortschritt) => uploadUnterweisungMedium(projectId, unterweisung.id, file, fortschritt),
    aufraeumen: async (vorher, nachher) => {
      for (const pfad of verwaistePfade(vorher, nachher, projectId)) {
        await loescheUnterweisungMedium(pfad).catch(() => undefined);
      }
    },
  };
  return <EditorKern ziel={ziel} isMobile={isMobile} onZurueck={onZurueck} />;
};

/**
 * Der Editor einer globalen Vorlage (Nr. 141) — dieselbe Oberflaeche, Ziel
 * vorlagenService.
 *
 * Die FASSUNG fuehrt der Editor selbst: gespeichert wird gegen die Fassung,
 * aus der der Entwurf stammt (optimistische Sperre). Eine neuere Fassung, die
 * der Container nachlaedt, wird deshalb NICHT still uebernommen — sonst
 * ueberschriebe das naechste Speichern die Aenderung eines anderen. Den Weg
 * zum neuen Stand geht „Neu laden" nach einem Konflikt. Ein Wechsel der id
 * (andere Vorlage) uebernimmt die Prop.
 *
 * Medien loescht nur loescheVorlagenMedium, und nur im eigenen Ordner, solange
 * die Vorlage nie freigegeben war (§2). Ein Statuswechsel zaehlt die Fassung
 * nicht hoch (setzeVorlagenStatus), ist aber an sie gebunden; Inhalt wird
 * vorher nur gespeichert, wenn er sich geaendert hat.
 */
export const VorlagenEditor: React.FC<VorlagenEditorProps> = ({
  vorlage,
  isReadOnly = false,
  isMobile = false,
  kategorien = [],
  onZurueck,
  onGespeichert,
}) => {
  const [gemerkt, setGemerkt] = useState(vorlage);
  const v = gemerkt.id === vorlage.id ? gemerkt : vorlage;
  const merke = (neu: SifaUnterweisungVorlage) => {
    setGemerkt(neu);
    onGespeichert(neu);
  };

  const ziel: EditorZiel<SifaUnterweisungVorlage> = {
    stand: v,
    ueberschrift: `Vorlage · ${v.titel || 'ohne Titel'}`,
    angaben: vorlageKopfAngaben(v),
    statusTexte: VORLAGEN_STATUS_LABEL,
    schreibbar: !isReadOnly,
    archivUmkehrbar: true,
    browserRueckfrage: false,
    testArt: 'vorlage',
    texte: {
      nurAnsicht: KEIN_RECHT_HINWEIS,
      archiviert: 'Diese Vorlage ist archiviert und schreibgeschützt. Beim Kunden steht sie nicht mehr zur Auswahl; bestehende Kopien laufen weiter.',
      kopfHinweis: freigabeHinweis(v),
      aktivKnopf: 'Freigeben',
      wiederAktivKnopf: 'Wieder freigeben',
      aktivErklaerung: 'Freigeben speichert den Stand mit. Danach können alle SiFa-Kunden die Vorlage übernehmen.',
      aktivErfolg: FREIGEGEBEN_MELDUNG,
      frageAktiv: vorlageMedienLoeschbar(v) ? FRAGE_ERSTE_FREIGABE : null,
      frageEntwurf: FRAGE_ZURUECK_AUF_ENTWURF,
      frageArchivieren: FRAGE_ARCHIVIEREN,
    },
    entwurfAus: entwurfAusVorlage,
    kategorie: { aus: (q) => q.kategorie ?? '', vorschlaege: kategorien },
    speichern: async ({ entwurf, kategorie, status, inhaltGeaendert }) => {
      const inhalt = inhaltGeaendert ? await updateVorlage(v.id, v.fassung, { ...entwurf, kategorie }) : v;
      if (!status || status === inhalt.status) return inhalt;
      try {
        // Gebunden an Fassung UND Status, die hier stehen: nach dem Speichern
        // die gespeicherten, sonst die, die der Nutzer gerade sieht. Hat
        // inzwischen jemand anderes gespeichert oder den Status gewechselt
        // (z. B. archiviert), gilt dieser Klick nicht still als Erfolg —
        // VorlageKonfliktError, „Neu laden".
        return await setzeVorlagenStatus(inhalt.id, status, inhalt.fassung, inhalt.status);
      } catch (e) {
        // Der Inhalt steht schon mit neuer Fassung: merken, sonst meldet das
        // naechste Speichern einen Konflikt mit sich selbst.
        if (inhalt !== v) merke(inhalt);
        throw e;
      }
    },
    uebernehmen: merke,
    hochladen: (file, fortschritt) => uploadVorlagenMedium(v.id, file, fortschritt),
    aufraeumen: async (vorher, nachher) => {
      // `nachher` entscheidet: wurde im selben Zug freigegeben, bleibt alles liegen.
      for (const pfad of verwaistePfade(vorher, nachher, vorlagenOrdner(nachher.id))) {
        await loescheVorlagenMedium(nachher, pfad).catch(() => undefined);
      }
    },
    konflikt: {
      erkennen: (e) => e instanceof VorlageKonfliktError,
      neuLaden: async () => {
        const frisch = await fetchVorlage(v.id);
        if (!frisch) throw new Error('Diese Vorlage gibt es nicht mehr, oder sie ist für dieses Konto nicht lesbar.');
        return frisch;
      },
    },
  };
  return <EditorKern ziel={ziel} isMobile={isMobile} onZurueck={onZurueck} />;
};

// ─── Die Oberflaeche ────────────────────────────────────────────────────────

/**
 * Der Editor: Kopfdaten, Abschnitte (Text, Bild und/oder Video), Fragen mit
 * Antworten, Richtig-Haekchen und „Stellen nach" (Abschnitt oder am Ende),
 * Statuswechsel. Wohin gespeichert wird, sagt das Ziel.
 *
 * ─── Medien ───────────────────────────────────────────────────────────────
 * Vorbild ist das Paketformat der Tablet-App: ein Abschnitt = EIN Video +
 * die Fragen zu diesem Abschnitt. Bilder und Videos gehen in den
 * OEFFENTLICHEN Medien-Bucket (Nr. 84; Kurs `<projekt>/<kurs>/…`, Vorlage
 * `vorlagen/<id>/…`), die Vorschau hier ist dieselbe Public-URL (medienUrl),
 * die der Teilnehmer spaeter bekommt. Ein Video laedt mit Fortschritt
 * (Bytes-%), weil 150 MB ohne Balken wie ein Haenger aussehen. „Entfernen"
 * loescht die Datei im Bucket mit (ziel.aufraeumen) — der Verweis im
 * Abschnitt ist die einzige Stelle, die sie kennt; scheitert das Loeschen,
 * bleibt der Verweis weg und die Datei ist verwaist (harmlos, kein
 * Datenverlust).
 *
 * ─── Speichern und Aktivieren sind zwei Dinge ─────────────────────────────
 * Gespeichert werden darf jeder halbe Entwurf. AKTIV geschaltet (Vorlage:
 * freigegeben) wird nur, was der Kunde auch durchfuehren kann (Titel,
 * mindestens ein Abschnitt, jede Frage mit einer richtigen Antwort —
 * pruefeEntwurf). Der Knopf ist sonst gesperrt und sagt, was fehlt.
 *
 * ─── Archiviert heisst eingefroren ────────────────────────────────────────
 * Unter einem archivierten Kurs stehen Zertifikate, die sagen, worauf
 * unterschrieben wurde. Der Editor sperrt dann vollstaendig; derselbe Riegel
 * sitzt in updateUnterweisung als WHERE-Bedingung. Eine archivierte Vorlage
 * ist ebenso schreibgeschuetzt, laesst sich aber wieder freigeben.
 *
 * Die Richtig-Flags stehen NUR hier und in der Datenbank; die oeffentliche
 * Teilnehmerseite bekommt sie nie (sifa_unterweisung_public).
 */
function EditorKern<T extends EditorQuelle>({ ziel, isMobile, onZurueck }: {
  ziel: EditorZiel<T>;
  isMobile: boolean;
  onZurueck: () => void;
}) {
  const stand = ziel.stand;
  const archiviert = stand.status === 'archiviert';
  const gesperrt = !ziel.schreibbar || archiviert;

  const [entwurf, setEntwurf] = useState<UnterweisungEntwurf>(() => ziel.entwurfAus(stand));
  const [kategorie, setKategorie] = useState(() => ziel.kategorie?.aus(stand) ?? '');
  const [dirty, setDirty] = useState(false);
  const [speichert, setSpeichert] = useState(false);
  const [statusWechsel, setStatusWechsel] = useState(false);
  /** Laufender Upload: welcher Abschnitt, welche Art, wie weit (0..100). */
  const [upload, setUpload] = useState<{ index: number; art: MedienArt; prozent: number } | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  /** Die optimistische Sperre griff (Vorlage) — eigene Meldung mit „Neu laden". */
  const [konflikt, setKonflikt] = useState<string | null>(null);
  const [laedtNeu, setLaedtNeu] = useState(false);
  const [rueckfrage, setRueckfrage] = useState<Rueckfrage | null>(null);
  const [testOffen, setTestOffen] = useState(false);
  const speichertRef = useRef(false);
  const bildInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const uploadZielRef = useRef<number | null>(null);

  // Wechselt der Datensatz (anderer Kurs geoeffnet), muss der Entwurf mit.
  useEffect(() => {
    setEntwurf(ziel.entwurfAus(stand));
    setKategorie(ziel.kategorie?.aus(stand) ?? '');
    setDirty(false);
    setFehler(null);
    setHinweis(null);
    setKonflikt(null);
    setRueckfrage(null);
    setTestOffen(false);
  }, [stand.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const aendern = (patch: Partial<UnterweisungEntwurf>) => {
    setEntwurf((prev) => ({ ...prev, ...patch }));
    setDirty(true);
    setHinweis(null);
  };

  const kategorieAendern = (wert: string) => {
    setKategorie(wert);
    setDirty(true);
    setHinweis(null);
  };

  // ─── Speichern / Status ───────────────────────────────────────────────────

  /** `beiKonflikt`: zusaetzlich zur Meldung, wenn die optimistische Sperre griff (Vorlage). */
  const speichern = async (status?: UnterweisungStatus, beiKonflikt?: () => void): Promise<T | null> => {
    // Archiviert ist eingefroren — offen bleibt nur der Weg zurueck, wo es ihn gibt.
    const wiederFrei = status === 'aktiv' && archiviert && ziel.archivUmkehrbar && ziel.schreibbar;
    if ((gesperrt && !wiederFrei) || speichertRef.current) return null;
    // Waehrend eines Uploads nicht speichern: der Pfad kommt erst danach in den
    // Entwurf, und das Zuruecksetzen nach dem Speichern wuerfe ihn wieder hinaus.
    if (upload !== null) {
      setFehler('Bitte warten, bis der Upload fertig ist — sonst fehlt die Datei im gespeicherten Stand.');
      return null;
    }
    speichertRef.current = true;
    if (status) setStatusWechsel(true); else setSpeichert(true);
    setFehler(null);
    setKonflikt(null);
    const vorher = stand;
    try {
      const sauber = bereinigeEntwurf(entwurf);
      if (!sauber.titel) throw new Error('Bitte einen Titel eingeben.');
      const gespeichert = await ziel.speichern({ entwurf: sauber, kategorie, status, inhaltGeaendert: dirty });
      setEntwurf(ziel.entwurfAus(gespeichert));
      setKategorie(ziel.kategorie?.aus(gespeichert) ?? '');
      setDirty(false);
      ziel.uebernehmen(gespeichert);
      // Medien, auf die der gespeicherte Stand nicht mehr zeigt (ersetzt oder
      // entfernt), erst JETZT aus dem Bucket nehmen — vorher haette ein
      // „Aenderungen verwerfen" auf eine geloeschte Datei gezeigt. Best effort.
      await ziel.aufraeumen(vorher, gespeichert);
      return gespeichert;
    } catch (e) {
      if (ziel.konflikt?.erkennen(e)) {
        setKonflikt(describeError(e));
        beiKonflikt?.();
      } else {
        setFehler(describeError(e));
      }
      return null;
    } finally {
      speichertRef.current = false;
      setSpeichert(false);
      setStatusWechsel(false);
    }
  };

  const aktivAusfuehren = async () => {
    const u = await speichern('aktiv');
    if (u) setHinweis(ziel.texte.aktivErfolg);
  };

  /**
   * Getestet wird der gespeicherte Stand — scheitert das Speichern, gibt es
   * keinen Test. Scheitert es an einem Konflikt, wird gleich angeboten, den
   * gespeicherten Stand (den des anderen) zu testen.
   */
  const speichernUndTesten = async () => {
    if (await speichern(undefined, () => nachfragen({ art: 'testenKonflikt' }))) setTestOffen(true);
  };

  /** Konflikt: frischen Stand holen, lokale Aenderungen verwerfen (Rueckfrage vorher). */
  const neuLaden = async () => {
    const k = ziel.konflikt;
    if (!k) return;
    setLaedtNeu(true);
    setFehler(null);
    try {
      const frisch = await k.neuLaden();
      setEntwurf(ziel.entwurfAus(frisch));
      setKategorie(ziel.kategorie?.aus(frisch) ?? '');
      setDirty(false);
      setKonflikt(null);
      ziel.uebernehmen(frisch);
      setHinweis('Der aktuelle Stand ist geladen.');
    } catch (e) {
      setFehler(describeError(e));
    } finally {
      setLaedtNeu(false);
    }
  };

  // ─── Abschnitte ───────────────────────────────────────────────────────────

  // Funktionales Update: der Upload endet nach einem `await`, und bis dahin
  // kann der Text desselben Abschnitts weitergetippt worden sein.
  const abschnittAendern = (i: number, patch: Partial<UnterweisungAbschnitt>) => {
    setEntwurf((prev) => ({ ...prev, inhalt: ersetzeAn(prev.inhalt, i, { ...prev.inhalt[i], ...patch }) }));
    setDirty(true);
    setHinweis(null);
  };

  // ─── Rueckfragen ──────────────────────────────────────────────────────────

  const rueckfrageText = (r: Rueckfrage): { text: string; ja: string; gefaehrlich: boolean; alternative?: string } => {
    switch (r.art) {
      case 'zurueck': return { text: 'Ungespeicherte Änderungen verwerfen?', ja: 'Verwerfen', gefaehrlich: true };
      case 'aktiv': return { text: ziel.texte.frageAktiv ?? '', ja: ziel.texte.aktivKnopf, gefaehrlich: false };
      case 'entwurf': return { text: ziel.texte.frageEntwurf, ja: 'Zurück auf Entwurf', gefaehrlich: false };
      case 'archivieren': return { text: ziel.texte.frageArchivieren, ja: 'Archivieren', gefaehrlich: false };
      case 'medium': return { text: r.medium === 'video' ? 'Video entfernen?' : 'Bild entfernen?', ja: 'Entfernen', gefaehrlich: true };
      case 'abschnitt': return { text: 'Abschnitt löschen?', ja: 'Löschen', gefaehrlich: true };
      case 'frage': return { text: 'Frage löschen?', ja: 'Löschen', gefaehrlich: true };
      case 'neuLaden': return {
        text: 'Neu laden verwirft die ungespeicherten Änderungen in diesem Editor. Danach den aktuellen Stand prüfen und die eigene Änderung bei Bedarf erneut eintragen.',
        ja: 'Verwerfen und neu laden',
        gefaehrlich: true,
      };
      case 'testen': return { text: FRAGE_ERST_SPEICHERN, ja: 'Speichern + Testen', gefaehrlich: false, alternative: 'Gespeicherten Stand testen' };
      case 'testenKonflikt': return { text: FRAGE_TESTEN_BEI_KONFLIKT, ja: 'Gespeicherten Stand testen', gefaehrlich: false };
    }
  };

  const ausfuehren = (r: Rueckfrage) => {
    switch (r.art) {
      case 'zurueck': onZurueck(); return;
      case 'aktiv': void aktivAusfuehren(); return;
      case 'entwurf': void speichern('entwurf'); return;
      case 'archivieren': void speichern('archiviert'); return;
      case 'medium': abschnittAendern(r.index, r.medium === 'video' ? { videoPfad: null } : { bildPfad: null }); return;
      case 'abschnitt': aendern({ inhalt: entferneAn(entwurf.inhalt, r.index) }); return;
      case 'frage': aendern({ fragen: entferneAn(entwurf.fragen, r.index) }); return;
      case 'neuLaden': void neuLaden(); return;
      case 'testen': void speichernUndTesten(); return;
      case 'testenKonflikt': setTestOffen(true); return;
    }
  };

  /** Der dritte Weg einer Rueckfrage — bisher nur „Gespeicherten Stand testen". */
  const alternativeAusfuehren = (r: Rueckfrage) => {
    if (r.art === 'testen') setTestOffen(true);
  };

  /**
   * Fragt vor einem Schritt nach. Projekt wie bisher ueber den Browser
   * (synchron); Vorlage, „Neu laden" und „Testen" in der App. Dort wird beim
   * Bestaetigen mit dem Stand des DANN aktuellen Renders ausgefuehrt — ein
   * Upload, der waehrend der offenen Frage fertig wurde, geht so nicht verloren.
   */
  const nachfragen = (r: Rueckfrage) => {
    if (ziel.browserRueckfrage && !NUR_IN_DER_APP.has(r.art)) {
      if (window.confirm(rueckfrageText(r).text)) ausfuehren(r);
      return;
    }
    setRueckfrage(r);
  };

  const zurueck = () => {
    if (dirty && !gesperrt) { nachfragen({ art: 'zurueck' }); return; }
    onZurueck();
  };

  const aktivSchalten = () => {
    const maengel = pruefeEntwurf(entwurf);
    if (maengel.length > 0) { setFehler(maengel.join(' ')); return; }
    if (ziel.texte.frageAktiv) nachfragen({ art: 'aktiv' });
    else void aktivAusfuehren();
  };

  const neuLadenFragen = () => {
    if (dirty) nachfragen({ art: 'neuLaden' });
    else void neuLaden();
  };

  const testen = () => {
    if (upload !== null) return;
    switch (testAblauf({ dirty, gesperrt, konflikt: konflikt !== null })) {
      case 'fragen': nachfragen({ art: 'testen' }); return;
      case 'konflikt': nachfragen({ art: 'testenKonflikt' }); return;
      default: setTestOffen(true);
    }
  };

  const mediumWaehlen = (i: number, art: MedienArt) => {
    uploadZielRef.current = i;
    (art === 'video' ? videoInputRef : bildInputRef).current?.click();
  };

  /**
   * Bild oder Video eines Abschnitts hochladen. Die Art kommt aus der Datei
   * (medienArt), nicht aus dem Knopf — ein .mov ohne MIME-Typ landet trotzdem
   * richtig. Ein vorhandenes Medium derselben Art wird im Bucket entfernt,
   * sobald das neue steht (Verweis zuerst, Datei danach).
   */
  const mediumHochladen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const i = uploadZielRef.current;
    if (!file || i === null) return;
    const art = medienArt(file);
    if (!art) {
      setFehler('Bitte ein Bild (JPG, PNG, WebP) oder ein Video (MP4, WebM, MOV) wählen.');
      return;
    }
    setUpload({ index: i, art, prozent: 0 });
    setFehler(null);
    try {
      const { path } = await ziel.hochladen(file, (geladen, gesamt) => {
        setUpload({ index: i, art, prozent: gesamt > 0 ? Math.min(100, Math.round((geladen * 100) / gesamt)) : 0 });
      });
      abschnittAendern(i, art === 'video' ? { videoPfad: path } : { bildPfad: path });
    } catch (err) {
      setFehler(describeError(err));
    } finally {
      setUpload(null);
    }
  };

  /** Nimmt nur den VERWEIS; die Datei raeumt `speichern` nach dem Schreiben (ziel.aufraeumen). */
  const mediumEntfernen = (i: number, art: MedienArt) => nachfragen({ art: 'medium', index: i, medium: art });

  // ─── Fragen ───────────────────────────────────────────────────────────────

  const frageAendern = (i: number, patch: Partial<UnterweisungFrage>) =>
    aendern({ fragen: ersetzeAn(entwurf.fragen, i, { ...entwurf.fragen[i], ...patch }) });

  const antwortAendern = (fi: number, ai: number, patch: { text?: string; richtig?: boolean }) => {
    const f = entwurf.fragen[fi];
    frageAendern(fi, { antworten: ersetzeAn(f.antworten, ai, { ...f.antworten[ai], ...patch }) });
  };

  // ─── Ansicht ──────────────────────────────────────────────────────────────

  const feld = `w-full px-3 py-2 ${isMobile ? 'min-h-[44px]' : ''} text-sm text-slate-800 bg-slate-50 rounded-lg outline-none focus:bg-white focus:ring-1 focus:ring-teal-300 disabled:text-slate-500`;
  const klein = 'p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30 text-slate-500';
  const maengel = pruefeEntwurf(entwurf);
  const kannAktiv = darfAktivieren(entwurf);
  const frageTexte = rueckfrage ? rueckfrageText(rueckfrage) : null;

  return (
    <div data-glass-view="sifa" className="flex flex-col h-full bg-slate-50">
      <div data-glass="kopf" className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3 shrink-0">
        <button onClick={zurueck} className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-slate-100 rounded-full transition-colors">
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-bold text-slate-800 truncate">{ziel.ueberschrift}</h1>
          <p className="text-xs text-slate-500 truncate">
            {[ziel.statusTexte[stand.status], ...ziel.angaben].join(' · ')}{dirty ? ' · ungespeichert' : ''}
          </p>
        </div>
        {/* Testen auch im Entwurf und in der Nur-Ansicht — der Test-Link schreibt nichts. */}
        <button
          onClick={testen}
          disabled={speichert || statusWechsel || upload !== null}
          title={upload !== null ? UPLOAD_LAEUFT : TEST_TOOLTIP}
          aria-label="Testen"
          className="min-h-[44px] min-w-[44px] px-3 flex items-center justify-center gap-2 text-sm font-bold text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-xl transition-colors disabled:opacity-40"
        >
          <FlaskConical className="w-4 h-4" /><span className="hidden sm:inline">Testen</span>
        </button>
        {!gesperrt && (
          <button
            onClick={() => void speichern()}
            disabled={speichert || !dirty || upload !== null}
            title={upload !== null ? UPLOAD_LAEUFT : undefined}
            className="min-h-[44px] px-4 flex items-center gap-2 text-sm font-bold text-white bg-teal-600 hover:bg-teal-700 rounded-xl transition-colors disabled:opacity-40"
          >
            {speichert ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Speichern
          </button>
        )}
      </div>
      {ziel.texte.kopfHinweis && (
        <div className="bg-blue-50 border-b border-blue-200 px-4 py-2 flex items-start gap-2 text-xs text-blue-800 shrink-0">
          <Info className="w-4 h-4 shrink-0 mt-px" /><span>{ziel.texte.kopfHinweis}</span>
        </div>
      )}

      <div className={`flex-1 overflow-y-auto p-4 space-y-4 ${isMobile ? 'pb-20' : ''}`}>
        {gesperrt && (
          <div className="p-3 bg-amber-50 border border-amber-300 rounded-xl text-xs text-amber-900">
            {archiviert ? ziel.texte.archiviert : ziel.texte.nurAnsicht}
          </div>
        )}
        {konflikt && (
          <div className="p-3 bg-amber-50 border border-amber-300 rounded-xl text-xs text-amber-900 flex flex-wrap items-center gap-2" role="alert">
            <AlertTriangle className="w-4 h-4 shrink-0" /><span className="flex-1 min-w-[12rem]">{konflikt}</span>
            <button onClick={neuLadenFragen} disabled={laedtNeu} className="min-h-[36px] px-3 flex items-center gap-1.5 text-xs font-bold text-amber-800 bg-white border border-amber-300 hover:bg-amber-100 rounded-lg disabled:opacity-40">
              {laedtNeu ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Neu laden
            </button>
          </div>
        )}
        {fehler && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-px" /><span className="flex-1">{fehler}</span>
            <button onClick={() => setFehler(null)} className="p-1"><X className="w-3.5 h-3.5" /></button>
          </div>
        )}
        {hinweis && (
          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-800 flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 shrink-0 mt-px" /><span className="flex-1">{hinweis}</span>
            <button onClick={() => setHinweis(null)} className="p-1"><X className="w-3.5 h-3.5" /></button>
          </div>
        )}

        {/* Kopfdaten */}
        <section className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3">
          <div>
            <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Titel</label>
            <input value={entwurf.titel} onChange={(e) => aendern({ titel: e.target.value })} disabled={gesperrt} placeholder="z. B. Brandschutz und Flucht­wege" className={feld} />
          </div>
          {ziel.kategorie && (
            <div>
              <label htmlFor="unterweisung-kategorie" className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Kategorie</label>
              <input id="unterweisung-kategorie" list="unterweisung-kategorien" value={kategorie} onChange={(e) => kategorieAendern(e.target.value)} disabled={gesperrt} placeholder="z. B. Brandschutz, Erste Hilfe, Gefahrstoffe" className={feld} />
              <datalist id="unterweisung-kategorien">
                {ziel.kategorie.vorschlaege.map((k) => <option key={k} value={k} />)}
              </datalist>
            </div>
          )}
          <div>
            <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Beschreibung (Begrüßung auf der Teilnehmerseite)</label>
            <textarea value={entwurf.beschreibung || ''} onChange={(e) => aendern({ beschreibung: e.target.value })} disabled={gesperrt} rows={2} className={feld} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Dauer (min)</label>
              <input type="number" inputMode="numeric" min={0} value={entwurf.dauerMin ?? ''} onChange={(e) => aendern({ dauerMin: e.target.value === '' ? null : Number(e.target.value) })} disabled={gesperrt} placeholder="ca." className={feld} />
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Gültig (Monate)</label>
              <input type="number" inputMode="numeric" min={0} value={entwurf.gueltigMonate} onChange={(e) => aendern({ gueltigMonate: Number(e.target.value) })} disabled={gesperrt} className={feld} title="0 = ohne Ablauf" />
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Bestehen ab (%)</label>
              <input type="number" inputMode="numeric" min={0} max={100} value={entwurf.bestehenProzent} onChange={(e) => aendern({ bestehenProzent: Number(e.target.value) })} disabled={gesperrt} className={feld} />
            </div>
          </div>
        </section>

        {/* Abschnitte */}
        <section className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-sm font-bold text-slate-800">Abschnitte <span className="text-xs font-medium text-slate-500">({entwurf.inhalt.length})</span></h2>
          </div>
          {entwurf.inhalt.length === 0 && (
            <p className="text-xs text-slate-500">Noch kein Abschnitt. Jeder Abschnitt ist eine Seite, die der Teilnehmer liest, bevor er weiterklicken kann.</p>
          )}
          {entwurf.inhalt.map((a, i) => (
            <div key={a.id} className="border border-slate-200 rounded-xl p-3 space-y-2 bg-slate-50/50">
              <div className="flex items-center gap-2">
                <span className="text-xs font-black text-slate-400 w-6">{i + 1}.</span>
                <input value={a.titel} onChange={(e) => abschnittAendern(i, { titel: e.target.value })} disabled={gesperrt} placeholder="Überschrift des Abschnitts" className={`${feld} bg-white`} />
                <button onClick={() => aendern({ inhalt: verschiebe(entwurf.inhalt, i, -1) })} disabled={gesperrt || i === 0} className={klein} title="Nach oben"><ArrowUp className="w-4 h-4" /></button>
                <button onClick={() => aendern({ inhalt: verschiebe(entwurf.inhalt, i, 1) })} disabled={gesperrt || i === entwurf.inhalt.length - 1} className={klein} title="Nach unten"><ArrowDown className="w-4 h-4" /></button>
                <button onClick={() => nachfragen({ art: 'abschnitt', index: i })} disabled={gesperrt} className={`${klein} hover:text-red-600`} title="Löschen"><Trash2 className="w-4 h-4" /></button>
              </div>
              <textarea value={a.text} onChange={(e) => abschnittAendern(i, { text: e.target.value })} disabled={gesperrt} rows={5} placeholder="Text des Abschnitts — Absätze durch Leerzeile trennen." className={`${feld} bg-white`} />
              {/* Video — Vorschau ueber dieselbe Public-URL, die der Teilnehmer bekommt */}
              {a.videoPfad && upload?.index !== i && (
                <video controls playsInline preload="metadata" src={medienUrl(a.videoPfad) ?? undefined} className="w-full max-h-72 rounded-lg bg-black border border-slate-200">
                  Dein Browser kann dieses Video nicht abspielen.
                </video>
              )}
              {a.bildPfad && upload?.index !== i && (
                <img src={medienUrl(a.bildPfad) ?? undefined} alt="" className="max-h-48 rounded-lg border border-slate-200" />
              )}
              {upload?.index === i && (
                <div className="space-y-1" aria-live="polite">
                  <div className="flex items-center gap-2 text-xs text-slate-600">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    {upload.art === 'video' ? 'Video' : 'Bild'} wird hochgeladen … {upload.prozent} %
                  </div>
                  <div className="h-1.5 w-full bg-slate-200 rounded-full overflow-hidden">
                    <div className="h-full bg-teal-600 transition-all" style={{ width: `${upload.prozent}%` }} />
                  </div>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <button onClick={() => mediumWaehlen(i, 'video')} disabled={gesperrt || upload !== null} className="min-h-[36px] px-3 flex items-center gap-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg disabled:opacity-40">
                  <Film className="w-3.5 h-3.5" />
                  {a.videoPfad ? 'Video ersetzen' : 'Video hochladen'}
                </button>
                {a.videoPfad && (
                  <button onClick={() => mediumEntfernen(i, 'video')} disabled={gesperrt} className="min-h-[36px] px-3 flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-red-600 rounded-lg disabled:opacity-40">
                    <X className="w-3.5 h-3.5" /> Video entfernen
                  </button>
                )}
                <button onClick={() => mediumWaehlen(i, 'bild')} disabled={gesperrt || upload !== null} className="min-h-[36px] px-3 flex items-center gap-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg disabled:opacity-40">
                  <ImagePlus className="w-3.5 h-3.5" />
                  {a.bildPfad ? 'Bild ersetzen' : 'Bild hochladen'}
                </button>
                {a.bildPfad && (
                  <>
                    <button onClick={() => mediumEntfernen(i, 'bild')} disabled={gesperrt} className="min-h-[36px] px-3 flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-red-600 rounded-lg disabled:opacity-40">
                      <X className="w-3.5 h-3.5" /> Bild entfernen
                    </button>
                    <span className="text-[11px] text-slate-400 flex items-center gap-1"><ImageIcon className="w-3 h-3" /> {a.bildPfad.split('/').pop()}</span>
                  </>
                )}
              </div>
            </div>
          ))}
          {/* Unter dem letzten Abschnitt: wer am Ende der Liste arbeitet, muss nicht zurueck nach oben. */}
          <button onClick={() => aendern({ inhalt: [...entwurf.inhalt, neuerAbschnitt(crypto.randomUUID())] })} disabled={gesperrt} className="min-h-[36px] px-3 flex items-center gap-1.5 text-xs font-bold text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-lg disabled:opacity-40">
            <Plus className="w-3.5 h-3.5" /> Abschnitt
          </button>
          <p className="text-[11px] text-slate-500">
            Video (MP4, WebM, MOV; bis 1 GB) und Bild (JPG, PNG, WebP; bis 10 MB) werden dem Teilnehmer im Abschnitt angezeigt.
            Ein Abschnitt mit Video gilt erst als gelesen, wenn das Video bis zum Ende lief — Vorspulen ist dort nicht möglich.
          </p>
          <input ref={bildInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => void mediumHochladen(e)} />
          <input ref={videoInputRef} type="file" accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov" className="hidden" onChange={(e) => void mediumHochladen(e)} />
        </section>

        {/* Fragen */}
        <section className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-sm font-bold text-slate-800">Fragen <span className="text-xs font-medium text-slate-500">({entwurf.fragen.length})</span></h2>
          </div>
          {entwurf.fragen.length === 0 && (
            <p className="text-xs text-slate-500">Ohne Fragen gilt die Unterweisung nach dem Lesen als bestanden. Mehrere richtige Antworten je Frage sind erlaubt.</p>
          )}
          {entwurf.fragen.map((f, fi) => (
            <div key={f.id} className="border border-slate-200 rounded-xl p-3 space-y-2 bg-slate-50/50">
              <div className="flex items-center gap-2">
                <span className="text-xs font-black text-slate-400 w-6">F{fi + 1}</span>
                <input value={f.frage} onChange={(e) => frageAendern(fi, { frage: e.target.value })} disabled={gesperrt} placeholder="Frage" className={`${feld} bg-white`} />
                <button onClick={() => aendern({ fragen: verschiebe(entwurf.fragen, fi, -1) })} disabled={gesperrt || fi === 0} className={klein} title="Nach oben"><ArrowUp className="w-4 h-4" /></button>
                <button onClick={() => aendern({ fragen: verschiebe(entwurf.fragen, fi, 1) })} disabled={gesperrt || fi === entwurf.fragen.length - 1} className={klein} title="Nach unten"><ArrowDown className="w-4 h-4" /></button>
                <button onClick={() => nachfragen({ art: 'frage', index: fi })} disabled={gesperrt} className={`${klein} hover:text-red-600`} title="Löschen"><Trash2 className="w-4 h-4" /></button>
              </div>
              {/* Stellen nach: Abschnitt (Frage folgt direkt darauf) oder am Ende — wie im Paket der Tablet-App */}
              <div className="flex items-center gap-2 pl-8">
                <label htmlFor={`stellen-${f.id}`} className="text-[11px] font-bold uppercase text-slate-500 shrink-0">Stellen nach</label>
                <select
                  id={`stellen-${f.id}`}
                  value={gueltigeAbschnittId(f.abschnittId, entwurf.inhalt) ?? AM_ENDE}
                  onChange={(e) => frageAendern(fi, { abschnittId: e.target.value || null })}
                  disabled={gesperrt}
                  className={`${feld} bg-white max-w-md`}
                >
                  <option value={AM_ENDE}>am Ende (nach allen Abschnitten)</option>
                  {entwurf.inhalt.map((a, ai) => (
                    <option key={a.id} value={a.id}>
                      Abschnitt {ai + 1}{a.titel.trim() ? ` – ${a.titel.trim()}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5 pl-8">
                {f.antworten.map((a, ai) => (
                  <div key={a.id} className="flex items-center gap-2">
                    <label className="flex items-center gap-1.5 shrink-0 text-[11px] font-semibold text-slate-600" title="Richtige Antwort">
                      <input type="checkbox" checked={a.richtig === true} onChange={(e) => antwortAendern(fi, ai, { richtig: e.target.checked })} disabled={gesperrt} className="w-4 h-4 accent-emerald-600" />
                      richtig
                    </label>
                    <input value={a.text} onChange={(e) => antwortAendern(fi, ai, { text: e.target.value })} disabled={gesperrt} placeholder={`Antwort ${ai + 1}`} className={`${feld} bg-white`} />
                    <button onClick={() => frageAendern(fi, { antworten: entferneAn(f.antworten, ai) })} disabled={gesperrt || f.antworten.length <= 2} className={`${klein} hover:text-red-600`} title="Antwort entfernen"><X className="w-4 h-4" /></button>
                  </div>
                ))}
                <button onClick={() => frageAendern(fi, { antworten: [...f.antworten, neueAntwort(crypto.randomUUID())] })} disabled={gesperrt} className="text-xs font-semibold text-teal-700 hover:underline disabled:opacity-40 flex items-center gap-1">
                  <Plus className="w-3 h-3" /> Antwort
                </button>
              </div>
            </div>
          ))}
          {/* Immer unter der letzten Frage (Betreiber 07.10.) — bei leerer Liste an derselben Stelle. */}
          <button onClick={() => aendern({ fragen: [...entwurf.fragen, neueFrage(crypto.randomUUID(), [crypto.randomUUID(), crypto.randomUUID(), crypto.randomUUID()])] })} disabled={gesperrt} className="min-h-[36px] px-3 flex items-center gap-1.5 text-xs font-bold text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-lg disabled:opacity-40">
            <Plus className="w-3.5 h-3.5" /> Frage
          </button>
        </section>

        {/* Status */}
        {ziel.schreibbar && (!archiviert || ziel.archivUmkehrbar) && (
          <section className="bg-white border border-slate-200 rounded-2xl p-4 space-y-2">
            <h2 className="text-sm font-bold text-slate-800">Status: {ziel.statusTexte[stand.status]}</h2>
            {stand.status !== 'aktiv' ? (
              <>
                {!kannAktiv && (
                  <ul className="text-xs text-amber-800 list-disc pl-5 space-y-0.5">
                    {maengel.map((m) => <li key={m}>{m}</li>)}
                  </ul>
                )}
                <div className="flex flex-wrap gap-2">
                  <button onClick={aktivSchalten} disabled={!kannAktiv || statusWechsel} className="min-h-[44px] px-4 flex items-center gap-2 text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl disabled:opacity-40">
                    {statusWechsel ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} {archiviert ? ziel.texte.wiederAktivKnopf : ziel.texte.aktivKnopf}
                  </button>
                  {ziel.archivUmkehrbar && !archiviert && (
                    <button onClick={() => nachfragen({ art: 'archivieren' })} disabled={statusWechsel} className="min-h-[44px] px-4 flex items-center gap-2 text-sm font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl disabled:opacity-40">
                      <Archive className="w-4 h-4" /> Archivieren
                    </button>
                  )}
                </div>
                {!archiviert && <p className="text-[11px] text-slate-500">{ziel.texte.aktivErklaerung}</p>}
              </>
            ) : (
              <div className="flex flex-wrap gap-2">
                <button onClick={() => nachfragen({ art: 'entwurf' })} disabled={statusWechsel} className="min-h-[44px] px-4 flex items-center gap-2 text-sm font-bold text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-xl disabled:opacity-40">
                  Zurück auf Entwurf
                </button>
                <button onClick={() => nachfragen({ art: 'archivieren' })} disabled={statusWechsel} className="min-h-[44px] px-4 flex items-center gap-2 text-sm font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl disabled:opacity-40">
                  {statusWechsel ? <Loader2 className="w-4 h-4 animate-spin" /> : <Archive className="w-4 h-4" />} Archivieren
                </button>
              </div>
            )}
          </section>
        )}
      </div>

      {rueckfrage && frageTexte && (
        <RueckfrageDialog
          text={frageTexte.text}
          jaText={frageTexte.ja}
          gefaehrlich={frageTexte.gefaehrlich}
          alternative={frageTexte.alternative
            ? { text: frageTexte.alternative, onWahl: () => { setRueckfrage(null); alternativeAusfuehren(rueckfrage); } }
            : undefined}
          onNein={() => setRueckfrage(null)}
          onJa={() => { setRueckfrage(null); ausfuehren(rueckfrage); }}
        />
      )}
      {testOffen && (
        <TestLinkDialog art={ziel.testArt} id={stand.id} titel={stand.titel} onSchliessen={() => setTestOffen(false)} />
      )}
    </div>
  );
}
